import React, { useState, useEffect, useMemo } from 'react';
import { Flex, Typography } from 'antd';
import { SbDrawer, SbForm, SbSelect, SbInput, SbButton, SbSpin } from 'uiCore';
import Permission from 'Components/Permission';
import useEmployeeDrawer from './useEmployeeDrawer';
import { ERROR_MESSAGES } from 'constants/employeeManagement.constants';
import { withSelectAllOption } from 'Utils/selectOptions';

const { Text } = Typography;

const EmployeeDrawerV2 = ({ open, employee, onClose, onSaveSuccess }) => {
  const {
    loading,
    saving,
    profileOptions,
    requisitionOptions,
    managerOptions,
    hrbpOptions,
    findManagerName,
    findHrbpName,
    findRequisitionTitle,
    loadDrawerData,
    updateEmployeeData,
  } = useEmployeeDrawer();

  useEffect(() => {
    loadDrawerData();
  }, [loadDrawerData]);

  const [form] = SbForm.useForm();

  const isEditMode = Boolean(employee?.complete);
  const drawerTitle = isEditMode ? 'Edit Role and Profile' : 'Assign Role and Profile';

  // Watched form values for derived fields
  const rolePositions = SbForm.useWatch('rolePositions', form);
  const reportingManagerId = SbForm.useWatch('reportingManagerId', form);
  const hrbpId = SbForm.useWatch('hrbpId', form);

  // Derived display values
  const roleTitle = useMemo(() => {
    if (employee?.employee_assigned_role_data?.role_position) {
      return employee.employee_assigned_role_data.role_title;
    }
    return findRequisitionTitle(rolePositions);
  }, [rolePositions, employee, findRequisitionTitle]);

  const managerName = useMemo(
    () => findManagerName(reportingManagerId),
    [reportingManagerId, findManagerName],
  );

  const hrbpName = useMemo(
    () => findHrbpName(hrbpId),
    [hrbpId, findHrbpName],
  );

  // Set initial form values when employee changes
  useEffect(() => {
    if (employee) {
      form.setFieldsValue({
        employeeId: employee.employee_data?.employee_id || '',
        employeeEmail: employee.employee_data?.employee_email_id || '',
        employeeName: `${employee.employee_data?.employee_first_name || ''} ${employee.employee_data?.employee_last_name || ''}`.trim(),
        rolePositions: employee.employee_assigned_role_data?.sb_unique_id || undefined,
        assignedProfiles: employee.roles?.map((r) => r.role_id) || [],
        reportingManagerId: employee.sb_reporting_manager_user_unique_id || undefined,
        hrbpId: employee.sb_hrbp_user_unique_id || undefined,
      });
    }
  }, [employee, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        hrbp_employee_id: values.hrbpId,
        reporting_manager_employee_id: values.reportingManagerId,
        roles: values.assignedProfiles.map((id) => ({ role_id: id })),
        employee_assigned_role_data: {
          sb_unique_id: values.rolePositions,
        },
      };

      const success = await updateEmployeeData({
        employeeId: employee.sb_user_unique_id,
        payload,
      });

      if (success) {
        onSaveSuccess?.();
        onClose();
      }
    } catch {
      // validation errors handled by form
    }
  };

  const profileSelectOptions = useMemo(
    () => withSelectAllOption({ value: 'all', label: 'All' }, profileOptions),
    [profileOptions],
  );

  const handleProfileChange = (selectedValues) => {
    if (selectedValues.includes('all')) {
      const allIds = profileOptions.map((p) => p.value);
      form.setFieldsValue({ assignedProfiles: allIds });
    }
  };

  const hasExistingPosition = Boolean(
    employee?.employee_assigned_role_data?.role_position,
  );

  const footer = (
    <Flex justify="flex-end" gap={8}>
      <SbButton buttonType="secondary" onClick={onClose} disabled={saving}>
        Cancel
      </SbButton>
      <Permission resource="employee" actions={['edit']}>
        <SbButton
          buttonType="primary"
          onClick={handleSave}
          loading={saving}
        >
          Save
        </SbButton>
      </Permission>
    </Flex>
  );

  return (
    <SbDrawer
      open={open}
      onClose={onClose}
      title={drawerTitle}
      width="50vw"
      footer={footer}
      destroyOnClose
    >
      {loading ? (
        <Flex justify="center" align="center">
          <SbSpin />
        </Flex>
      ) : (
        <SbForm form={form} layout="vertical" autoComplete="off">
          <SbForm.Item label="Employee ID" name="employeeId">
            <SbInput disabled />
          </SbForm.Item>

          <SbForm.Item label="Employee Email" name="employeeEmail">
            <SbInput disabled />
          </SbForm.Item>

          <SbForm.Item label="Employee Name" name="employeeName">
            <SbInput disabled />
          </SbForm.Item>

          {hasExistingPosition ? (
            <SbForm.Item label="Role Position #" name="rolePositions">
              <SbInput disabled />
            </SbForm.Item>
          ) : (
            <SbForm.Item
              label="Role Position #"
              name="rolePositions"
              rules={[{ required: true, message: ERROR_MESSAGES.SELECT_PROFILE_ASSIGNMENT }]}
            >
              <SbSelect
                options={requisitionOptions}
                placeholder="Search Role Position #"
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </SbForm.Item>
          )}

          <SbForm.Item label="Role Title">
            <SbInput value={roleTitle} disabled />
          </SbForm.Item>

          <SbForm.Item
            label="Assigned Profiles"
            name="assignedProfiles"
            rules={[{ required: true, message: ERROR_MESSAGES.SELECT_AT_LEAST_ONE_ROLE }]}
          >
            <SbSelect
              mode="multiple"
              options={profileSelectOptions}
              placeholder="Select profile/s (multiple selection is allowed)"
              optionFilterProp="label"
              onChange={handleProfileChange}
            />
          </SbForm.Item>

          <Flex gap={16}>
            <Flex vertical flex={1}>
              <SbForm.Item
                label="Reporting Manager Emp ID"
                name="reportingManagerId"
                rules={[{ required: true, message: ERROR_MESSAGES.SELECT_REPORTING_MANAGER }]}
              >
                <SbSelect
                  options={managerOptions}
                  placeholder="Search or select Reporting Manager"
                  showSearch
                  optionFilterProp="label"
                  allowClear
                />
              </SbForm.Item>
            </Flex>
            <Flex vertical flex={1}>
              <SbForm.Item label="Reporting Manager Name">
                <SbInput value={managerName} disabled />
              </SbForm.Item>
            </Flex>
          </Flex>

          <Flex gap={16}>
            <Flex vertical flex={1}>
              <SbForm.Item
                label="HRBP Emp ID"
                name="hrbpId"
                rules={[{ required: true, message: ERROR_MESSAGES.SELECT_HRBP }]}
              >
                <SbSelect
                  options={hrbpOptions}
                  placeholder="Search or select HRBP Emp ID"
                  showSearch
                  optionFilterProp="label"
                  allowClear
                />
              </SbForm.Item>
            </Flex>
            <Flex vertical flex={1}>
              <SbForm.Item label="HRBP Name">
                <SbInput value={hrbpName} disabled />
              </SbForm.Item>
            </Flex>
          </Flex>
        </SbForm>
      )}
    </SbDrawer>
  );
};

export default EmployeeDrawerV2;
